import { Component, computed, inject, signal, ChangeDetectionStrategy, OnInit, HostListener, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReleaseNotesService } from '@core/services/release-notes.service';
import { ReleaseNote } from '@core/models/release-note.model';
import { AppIconComponent } from '@shared/components/app-icon/app-icon.component';

/**
 * Modale "Quoi de neuf" — affichée automatiquement quand l'utilisateur a une
 * ou plusieurs notes de version non lues. Les notes sont défilées une par une
 * via "Suivant", et chacune n'est marquée comme lue côté serveur que lors
 * d'un acquittement explicite via le bouton "J'ai compris" (ou
 * "Tout marquer comme lu").
 *
 * La fermeture par la croix, le clic à l'extérieur ou la touche Échap est
 * purement locale : la note réapparaîtra à la prochaine connexion, sur cet
 * appareil comme sur les autres (la source de vérité reste le backend).
 *
 * Posé une seule fois dans `app.component.ts`, ne s'affiche que si la liste
 * non lue n'est pas vide → aucun coût visuel pour les utilisateurs à jour.
 */
@Component({
  selector: 'app-release-notes-modal',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div *ngIf="currentNote() as note"
         class="modal-overlay"
         role="dialog"
         aria-modal="true"
         [attr.aria-labelledby]="'release-note-title-' + note.id"
         (click)="onOverlayClick($event)">
      <div class="modal release-note-modal" (click)="$event.stopPropagation()">
        <button type="button"
                class="release-note-modal__close"
                aria-label="Fermer"
                (click)="close()"
                [disabled]="busy()">
          <app-icon name="X" size="sm"></app-icon>
        </button>
        <header class="release-note-modal__header">
          <div class="release-note-modal__badge"
               [class.is-feature]="note.severity === 1"
               [class.is-breaking]="note.severity === 2">
            <app-icon [name]="iconFor(note.severity)" size="sm"></app-icon>
            <span>v{{ note.version }}</span>
          </div>
          <h2 [id]="'release-note-title-' + note.id" class="release-note-modal__title">
            {{ note.title }}
          </h2>
          <p class="release-note-modal__date">
            {{ formatDate(note.publishedAt) }}
          </p>
        </header>

        <section class="release-note-modal__body">
          <p *ngIf="note.summary" class="release-note-modal__summary">{{ note.summary }}</p>
          <!-- ContentHtml est déjà sanitizé côté serveur (Markdig + HtmlSanitizer). -->
          <div class="release-note-modal__content" [innerHTML]="note.contentHtml"></div>
        </section>

        <footer class="release-note-modal__footer">
          <span class="release-note-modal__progress" *ngIf="total() > 1">
            {{ index() + 1 }} / {{ total() }}
          </span>
          <div class="release-note-modal__actions">
            <button *ngIf="total() > 1"
                    type="button"
                    class="btn btn-ghost"
                    (click)="markAllAsRead()"
                    [disabled]="busy()">
              Tout marquer comme lu
            </button>
            <button type="button"
                    class="btn btn-primary"
                    (click)="acknowledge()"
                    [disabled]="busy()">
              {{ hasNext() ? 'Suivant' : 'J\\'ai compris' }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 1100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: var(--bg-overlay, rgba(15, 23, 42, 0.55));
      backdrop-filter: blur(3px);
      -webkit-backdrop-filter: blur(3px);
      animation: rn-fade 0.15s ease-out;
    }

    .release-note-modal {
      position: relative;
      width: min(720px, 100%);
      max-height: min(90vh, 760px);
      display: flex;
      flex-direction: column;
      background: var(--bg-surface);
      color: var(--text-primary);
      border: 1px solid var(--border-default);
      border-radius: 14px;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.04) inset,
        0 24px 60px rgba(15, 23, 42, 0.45),
        0 8px 16px rgba(15, 23, 42, 0.20);
      overflow: hidden;
      animation: rn-pop 0.18s ease-out;
    }

    .release-note-modal__close {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      color: var(--text-tertiary);
      border-radius: 8px;
      cursor: pointer;
      z-index: 1;
      transition: background 0.15s, color 0.15s;
    }
    .release-note-modal__close:hover:not(:disabled) {
      background: var(--bg-surface-2);
      color: var(--text-primary);
    }
    .release-note-modal__close:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }
    .release-note-modal__close:disabled { opacity: 0.5; cursor: not-allowed; }

    .release-note-modal__header {
      padding: 22px 26px 18px;
      border-bottom: 1px solid var(--border-subtle);
      background: var(--bg-surface-2);
    }

    .release-note-modal__badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.02em;
      background: var(--color-primary-tint);
      color: var(--color-primary-hover);
      margin-bottom: 10px;
    }
    .release-note-modal__badge.is-feature {
      background: var(--color-administrative-tint, var(--color-primary-tint));
      color: var(--color-administrative, var(--color-primary-hover));
    }
    .release-note-modal__badge.is-breaking {
      background: var(--color-warehouse-tint, var(--color-primary-tint));
      color: var(--color-warehouse, var(--color-primary-hover));
    }

    .release-note-modal__title {
      margin: 0;
      font-size: 1.3rem;
      font-weight: 700;
      line-height: 1.3;
      color: var(--text-primary);
      letter-spacing: -0.01em;
    }
    .release-note-modal__date {
      margin: 6px 0 0;
      color: var(--text-tertiary);
      font-size: 0.85rem;
    }

    .release-note-modal__body {
      padding: 20px 26px;
      overflow-y: auto;
      flex: 1 1 auto;
      min-height: 0;
      color: var(--text-primary);
      scrollbar-width: thin;
      scrollbar-color: var(--border-strong) transparent;
    }
    .release-note-modal__body::-webkit-scrollbar { width: 10px; }
    .release-note-modal__body::-webkit-scrollbar-track { background: transparent; }
    .release-note-modal__body::-webkit-scrollbar-thumb {
      background: var(--border-strong);
      border-radius: 999px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    .release-note-modal__body::-webkit-scrollbar-thumb:hover {
      background: var(--text-tertiary);
      background-clip: padding-box;
      border: 2px solid transparent;
    }

    .release-note-modal__summary {
      font-weight: 500;
      font-size: 0.98rem;
      color: var(--text-secondary);
      margin: 0 0 16px;
      padding-bottom: 14px;
      border-bottom: 1px solid var(--border-subtle);
      line-height: 1.5;
    }

    .release-note-modal__content {
      line-height: 1.65;
      color: var(--text-primary);
      font-size: 0.95rem;
    }
    .release-note-modal__content :is(h1,h2,h3) {
      margin: 22px 0 10px;
      line-height: 1.3;
      color: var(--text-primary);
      font-weight: 700;
      letter-spacing: -0.005em;
    }
    .release-note-modal__content h1 { font-size: 1.18rem; }
    .release-note-modal__content h2 {
      font-size: 1.08rem;
      padding-bottom: 4px;
      border-bottom: 1px solid var(--border-subtle);
    }
    .release-note-modal__content h3 { font-size: 1rem; }
    .release-note-modal__content :is(h1,h2,h3):first-child { margin-top: 0; }
    .release-note-modal__content p {
      margin: 0 0 12px;
      color: var(--text-primary);
    }
    .release-note-modal__content strong { color: var(--text-primary); font-weight: 600; }
    .release-note-modal__content ul,
    .release-note-modal__content ol {
      padding-left: 1.4rem;
      margin: 0 0 14px;
    }
    .release-note-modal__content li {
      margin-bottom: 6px;
      color: var(--text-primary);
    }
    .release-note-modal__content li::marker {
      color: var(--color-primary);
    }
    .release-note-modal__content code {
      background: var(--bg-muted);
      color: var(--text-primary);
      padding: 1px 6px;
      border-radius: 4px;
      font-size: 0.88em;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      border: 1px solid var(--border-subtle);
    }
    .release-note-modal__content hr {
      border: none;
      border-top: 1px solid var(--border-subtle);
      margin: 18px 0;
    }
    .release-note-modal__content a {
      color: var(--color-primary);
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 2px;
    }
    .release-note-modal__content a:hover {
      color: var(--color-primary-hover);
    }

    .release-note-modal__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 26px;
      border-top: 1px solid var(--border-subtle);
      background: var(--bg-surface-2);
      flex-wrap: wrap;
    }
    .release-note-modal__progress {
      font-size: 0.85rem;
      color: var(--text-tertiary);
      font-weight: 500;
    }
    .release-note-modal__actions {
      display: flex;
      gap: 8px;
      margin-left: auto;
      flex-wrap: wrap;
    }

    .release-note-modal__footer .btn {
      padding: 8px 18px;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      border: 1px solid transparent;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }
    .release-note-modal__footer .btn:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }
    .release-note-modal__footer .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .release-note-modal__footer .btn-ghost {
      background: transparent;
      color: var(--text-secondary);
      border-color: var(--border-default);
    }
    .release-note-modal__footer .btn-ghost:hover:not(:disabled) {
      background: var(--bg-surface);
      color: var(--text-primary);
      border-color: var(--border-strong);
    }
    .release-note-modal__footer .btn-primary {
      background: var(--color-primary);
      color: var(--text-on-primary);
      border-color: var(--color-primary);
    }
    .release-note-modal__footer .btn-primary:hover:not(:disabled) {
      background: var(--color-primary-hover);
      border-color: var(--color-primary-hover);
    }

    @media (max-width: 600px) {
      .release-note-modal__header,
      .release-note-modal__body,
      .release-note-modal__footer { padding-left: 18px; padding-right: 18px; }
      .release-note-modal__title { font-size: 1.15rem; }
    }

    @keyframes rn-fade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes rn-pop {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
  `]
})
export class ReleaseNotesModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(ReleaseNotesService);

  readonly notes = signal<ReleaseNote[]>([]);
  readonly index = signal(0);
  readonly busy = signal(false);

  readonly total = computed(() => this.notes().length);
  readonly currentNote = computed(() => this.notes()[this.index()] ?? null);
  readonly hasNext = computed(() => this.index() < this.total() - 1);

  ngOnInit(): void {
    this.service.unread$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      // On ouvre la modale uniquement si la liste change réellement.
      // Les notes individuellement marquées "lues" sont retirées par le service,
      // ce qui fait avancer naturellement la pile.
      if (!list.length) {
        this.notes.set([]);
        this.index.set(0);
        return;
      }
      // Conserver la pile au moment de l'ouverture pour ne pas la voir muter
      // pendant la lecture (sauf au logout/clear → reset).
      if (!this.notes().length) {
        this.notes.set([...list]);
        this.index.set(0);
      }
    });
  }

  acknowledge(): void {
    const note = this.currentNote();
    if (!note || this.busy()) return;
    this.busy.set(true);
    this.service.markAsRead(note.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busy.set(false);
        if (this.hasNext()) {
          this.index.update(i => i + 1);
        } else {
          this.notes.set([]);
          this.index.set(0);
        }
      },
      error: () => this.busy.set(false)
    });
  }

  markAllAsRead(): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.service.markAllAsRead().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busy.set(false);
        this.notes.set([]);
        this.index.set(0);
      },
      error: () => this.busy.set(false)
    });
  }

  /**
   * Fermeture sans acquittement (croix, clic à l'extérieur, touche Échap).
   * On masque la popin uniquement côté client : aucune entrée n'est créée
   * dans `UserReleaseNoteView`, donc la note réapparaîtra à la prochaine
   * connexion (sur cet appareil comme sur les autres).
   */
  close(): void {
    if (!this.notes().length) return;
    this.notes.set([]);
    this.index.set(0);
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.currentNote()) {
      this.close();
    }
  }

  formatDate(value: string | Date | null | undefined): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  iconFor(severity: number): string {
    switch (severity) {
      case 2: return 'AlertTriangle';
      case 1: return 'Star';
      default: return 'Megaphone';
    }
  }
}
